/**
 * mse.ts — Section 2: "MSE, RMSE, and MAE — Three Ways to Score an Error".
 *
 * Same scatter as Section 1, plus residual bars from each point to the OLS fit
 * (red for above the line, green for below). One designated outlier point is
 * draggable — grab it and the three error metrics respond very differently:
 *
 *   - MSE / RMSE grow *fast* (squared residuals → large errors dominate).
 *   - MAE grows *linearly* (robust to the outlier).
 *
 * Two bar readouts side-by-side (MSE vs MAE, both on the same scale) make the
 * asymmetry visible: as the outlier moves away, the MSE bar rockets past MAE.
 *
 * The outlier is the LAST generated point. The fit recomputes from scratch on
 * every drag — no closed-form leverage shortcut — so the line visibly tilts
 * toward the dragged point, which is itself the lesson (OLS gives outliers huge
 * leverage under squared loss).
 */

import { regressionPoints, ols, type Point } from './data';

const X_MIN = 0;
const X_MAX = 10;
const Y_MIN = -4;
const Y_MAX = 16; // extra headroom so a dragged outlier stays on-canvas

export function initMse(): void {
  const canvas = document.getElementById('mseCanvas') as HTMLCanvasElement | null;
  const mseBar = document.getElementById('mseBar');
  const rmseBar = document.getElementById('rmseBar');
  const maeBar = document.getElementById('maeBar');
  const mseValue = document.getElementById('mseValue');
  const rmseValue = document.getElementById('rmseValue');
  const maeValue = document.getElementById('maeValue');
  const statusEl = document.getElementById('mseStatus');
  if (
    !canvas || !mseBar || !rmseBar || !maeBar ||
    !mseValue || !rmseValue || !maeValue || !statusEl
  ) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Account for the canvas's CSS scaling (responsive width). Without this the
  // pointer → data mapping drifts on wide screens. `canvas` is narrowed to
  // HTMLCanvasElement here; reference it through the alias so TS keeps the
  // narrowing inside this closure.
  const cv = canvas;
  function toDataCoords(evt: PointerEvent): { x: number; y: number } {
    const rect = cv.getBoundingClientRect();
    const scaleX = cv.width / rect.width;
    const scaleY = cv.height / rect.height;
    const px = (evt.clientX - rect.left) * scaleX;
    const py = (evt.clientY - rect.top) * scaleY;
    const PAD = 40;
    const x = X_MIN + ((px - PAD) / (cv.width - 2 * PAD)) * (X_MAX - X_MIN);
    const y = Y_MAX - ((py - PAD) / (cv.height - 2 * PAD)) * (Y_MAX - Y_MIN);
    return { x, y };
  }

  const W = canvas.width;
  const H = canvas.height;
  const PAD = 40;
  const mapX = (x: number): number => PAD + ((x - X_MIN) / (X_MAX - X_MIN)) * (W - 2 * PAD);
  const mapY = (y: number): number => H - PAD - ((y - Y_MIN) / (Y_MAX - Y_MIN)) * (H - 2 * PAD);

  // The points live on a state object so the drag handler can mutate the
  // outlier in place. Regenerate once; the outlier is index N-1.
  const state: { points: Point[]; dragging: boolean } = {
    points: regressionPoints(1),
    dragging: false,
  };

  // Scale for the bars. Bar widths are in % of their container; we normalise
  // against a generous max so a flung outlier still fits on screen.
  const BAR_MAX = 40; // an MSE of ~40 already fills the bar

  function drawAxes(): void {
    ctx!.strokeStyle = '#e2e8f0';
    ctx!.lineWidth = 1;
    ctx!.beginPath();
    for (let y = Y_MIN; y <= Y_MAX; y += 4) {
      ctx!.moveTo(mapX(X_MIN), mapY(y));
      ctx!.lineTo(mapX(X_MAX), mapY(y));
    }
    for (let x = X_MIN; x <= X_MAX; x += 2) {
      ctx!.moveTo(mapX(x), mapY(Y_MIN));
      ctx!.lineTo(mapX(x), mapY(Y_MAX));
    }
    ctx!.stroke();
  }

  function draw(): void {
    const points = state.points;
    const { slope, intercept } = ols(points);

    // Errors — keep this in sync with the bar readouts below.
    let se = 0; // sum of squared errors
    let ae = 0; // sum of absolute errors
    for (const p of points) {
      const r = p.y - (slope * p.x + intercept);
      se += r * r;
      ae += Math.abs(r);
    }
    const n = points.length;
    const mse = se / n;
    const rmse = Math.sqrt(mse);
    const mae = ae / n;

    ctx!.clearRect(0, 0, W, H);
    drawAxes();

    // Best-fit line.
    ctx!.strokeStyle = '#4c72b0';
    ctx!.lineWidth = 3;
    ctx!.beginPath();
    ctx!.moveTo(mapX(X_MIN), mapY(slope * X_MIN + intercept));
    ctx!.lineTo(mapX(X_MAX), mapY(slope * X_MAX + intercept));
    ctx!.stroke();

    // Residual bars: rose above the line, emerald below. The outlier (last
    // point) gets a thicker, more saturated bar so the eye finds it.
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const pred = slope * p.x + intercept;
      const above = p.y > pred;
      ctx!.strokeStyle = above ? '#c44e52' : '#55a868';
      ctx!.lineWidth = i === points.length - 1 ? 3 : 1.5;
      ctx!.beginPath();
      ctx!.moveTo(mapX(p.x), mapY(p.y));
      ctx!.lineTo(mapX(p.x), mapY(pred));
      ctx!.stroke();
    }

    // Points. The outlier is ringed so it reads as draggable.
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      ctx!.fillStyle = '#4c72b0';
      ctx!.beginPath();
      ctx!.arc(mapX(p.x), mapY(p.y), 4, 0, Math.PI * 2);
      ctx!.fill();
      if (i === points.length - 1) {
        // Hollow rose ring around the outlier.
        ctx!.strokeStyle = '#c44e52';
        ctx!.lineWidth = 2;
        ctx!.beginPath();
        ctx!.arc(mapX(p.x), mapY(p.y), 8, 0, Math.PI * 2);
        ctx!.stroke();
      }
    }

    // Numeric readouts.
    mseValue!.textContent = mse.toFixed(2);
    rmseValue!.textContent = rmse.toFixed(2);
    maeValue!.textContent = mae.toFixed(2);

    // Bars — clamped to [0, 100]%. MSE/RMSE on one scale, MAE on the same
    // scale so the heights are directly comparable.
    mseBar!.style.width = `${Math.min(100, (mse / BAR_MAX) * 100)}%`;
    rmseBar!.style.width = `${Math.min(100, (rmse / BAR_MAX) * 100)}%`;
    maeBar!.style.width = `${Math.min(100, (mae / BAR_MAX) * 100)}%`;

    // Status: call out the asymmetry. MSE > MAE always (Jensen), but the *gap*
    // widens as the outlier pulls away — that's the visual.
    const gap = mse - mae * mae; // rough: MSE ≥ MAE² under specific assumptions
    if (mse > mae * 2.2) {
      statusEl!.textContent = 'The outlier dominates: MSE is far above MAE. Squared errors reward large mistakes with outsized influence.';
      statusEl!.className = 'text-sm mt-3 font-semibold text-rose-600';
    } else if (gap > 0) {
      statusEl!.textContent = 'Errors are moderate. MSE and MAE track each other; no single point is in charge.';
      statusEl!.className = 'text-sm mt-3 font-semibold text-amber-600';
    } else {
      statusEl!.textContent = 'Errors are tiny and even. MAE ≈ RMSE — the model fits cleanly with no standout mistake.';
      statusEl!.className = 'text-sm mt-3 font-semibold text-emerald-600';
    }
  }

  // --- Drag handling --------------------------------------------------------
  // Only the outlier (last point) is draggable. We grab on pointerdown within
  // ~14 px of it, move it on pointermove, and release on pointerup/leave.
  function pointerNearOutlier(evt: PointerEvent): boolean {
    const last = state.points[state.points.length - 1];
    const { x, y } = toDataCoords(evt);
    // Compare in data space — 0.6 units is a comfortable grab radius.
    return Math.hypot(x - last.x, y - last.y) < 0.6;
  }

  canvas.addEventListener('pointerdown', (evt) => {
    if (pointerNearOutlier(evt)) {
      state.dragging = true;
      canvas.setPointerCapture(evt.pointerId);
      canvas.style.cursor = 'grabbing';
    }
  });

  canvas.addEventListener('pointermove', (evt) => {
    if (state.dragging) {
      const { x, y } = toDataCoords(evt);
      const last = state.points[state.points.length - 1];
      last.x = Math.max(X_MIN + 0.1, Math.min(X_MAX - 0.1, x));
      last.y = Math.max(Y_MIN + 0.1, Math.min(Y_MAX - 0.1, y));
      draw();
    } else {
      canvas.style.cursor = pointerNearOutlier(evt) ? 'grab' : 'default';
    }
  });

  const release = (evt: PointerEvent): void => {
    state.dragging = false;
    canvas.style.cursor = pointerNearOutlier(evt) ? 'grab' : 'default';
  };
  canvas.addEventListener('pointerup', release);
  canvas.addEventListener('pointercancel', release);

  // A reset puts the outlier back to its generated position.
  const resetBtn = document.getElementById('mseResetBtn') as HTMLButtonElement | null;
  resetBtn?.addEventListener('click', () => {
    state.points = regressionPoints(1);
    draw();
  });

  draw();
}
